import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Map, Clock } from "lucide-react";

const Footer = () => {
  const [animated, setAnimated] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  const contacts = [
    { icon: <Phone className="w-4 h-4 text-sky-600" />, number: "8788061100" },
    { icon: <Phone className="w-4 h-4 text-sky-600" />, number: "020-24366858" },
    { icon: <Phone className="w-4 h-4 text-sky-600" />, number: "020-24365287" }
  ];

  useEffect(() => {
    // Add a small delay before triggering animations for better page load experience
    const timer = setTimeout(() => {
      setAnimated(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const footerLinks = [
    { title: "Quick Links", items: ["Home", "About", "Specialities", "Contact", "Gallery", "Review"] },
    { title: "Our Services", items: ["Eye Care", "Orthopedic Care"] },
  ];

  // Animation classes with enhanced effects
  const fadeInClass = animated ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8";

  return (
    <footer className="relative mt-16 overflow-hidden">
      {/* Decorative wave SVG at the top */}
      <div className="absolute top-0 left-0 w-full overflow-hidden line-height-0">
        <svg className="fill-sky-700" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V56.44Z"></path>
        </svg>
      </div>
      
      {/* Main Footer Section with gradient background */}
      <div className="pt-16 bg-gradient-to-b from-sky-700 to-sky-900">
        <div className="container mx-auto px-3 py-2">
          {/* Logo and tagline */}
          <div className={`text-center mb-12 transition-all duration-700 ease-out ${fadeInClass}`}>
            <h2 className="text-black bg-white inline-block px-3 py-2 rounded-lg shadow-lg text-3xl font-bold mb-3 transform hover:scale-105 transition-transform duration-300">Unity Ortho and Eye Hospital</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Contact Info Section */}
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Opening Hours Card */}
                <div className={`transition-all duration-700 ease-out delay-200 ${fadeInClass}`}>
                  <div className="backdrop-blur-sm bg-white/90 rounded-xl p-6 shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 w-full">
                    <div className="flex items-center mb-4">
                      <div className="bg-sky-700 p-2 rounded-lg mr-3 shrink-0">
                        <Clock className="w-6 h-6 text-white" />
                      </div>
                      <h4 className="font-bold text-black text-lg">Opening Hours</h4>
                    </div>

                    {/* Ortho Hospital Timing */}
                    <div className="mb-4">
                      <h5 className="font-semibold text-sky-700 mb-2">Ortho Hospital</h5>
                      <ul className="space-y-2 text-black pl-3 border-l-2 border-sky-500">
                        <li className="flex flex-wrap items-center">
                          <span className="w-28 text-sky-700 font-semibold">Mon - Sat:</span>
                          <span>11:00 AM - 1:00 PM</span>
                        </li>
                        <li className="flex flex-wrap items-center">
                          <span className="w-28 text-sky-700 font-semibold">Evening:</span>
                          <span>7:00 PM - 9:00 PM</span>
                        </li>
                        <li className="flex flex-wrap items-center">
                          <span className="w-28 text-sky-700 font-semibold">Sunday:</span>
                          <span>Closed</span>
                        </li>
                      </ul>
                    </div>

                    {/* Eye Hospital Timing */}
                    <div>
                      <h5 className="font-semibold text-sky-700 mb-2">Eye Hospital</h5>
                      <ul className="space-y-2 text-black pl-3 border-l-2 border-sky-500">
                        <li className="flex flex-wrap items-center">
                          <span className="w-28 text-sky-700 font-semibold">Mon - Sat:</span>
                          <span>11:00 AM - 2:00 PM</span>
                        </li>
                        <li className="flex flex-wrap items-center">
                          <span className="w-28 text-sky-700 font-semibold">Evening:</span>
                          <span>6:00 PM - 8:00 PM</span>
                        </li>
                        <li className="flex flex-wrap items-center">
                          <span className="w-28 text-sky-700 font-semibold">Sunday:</span>
                          <span>Closed</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Address and Contact Numbers in a column */}
                <div className="flex flex-col gap-3">
                  {/* Address Card */}
                  <div className={`transition-all duration-700 ease-out delay-300 ${fadeInClass}`}>
                    <div className="backdrop-blur-sm bg-white/90 rounded-xl p-6 shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 w-full">
                      <div className="flex items-center mb-3">
                        <div className="bg-sky-700 p-2 rounded-lg mr-3 shrink-0">
                          <MapPin className="w-6 h-6 text-white" />
                        </div>
                        <h4 className="font-bold text-black text-lg">Address</h4>
                      </div>
                      <p className="text-black mb-3 text-sm">
                        Rajdhani Complex, Pune - Satara Rd,<br />
                        near Shri Shankar Maharaj Math,<br />
                        Pune, Maharashtra 411043
                      </p>
                      <button className="mt-1 text-white bg-sky-600 hover:bg-sky-500 px-4 py-2 rounded-lg text-sm font-medium flex items-center transition-all duration-300 hover:shadow-lg transform hover:translate-x-1">
                        <Map className="w-4 h-4 mr-2" />
                        Get Directions
                      </button>
                    </div>
                  </div>
                  
                  {/* Contact Numbers Card */}
                  <div className="transition-all duration-700 ease-out delay-400">
      <div className="backdrop-blur-sm bg-white/90 rounded-xl p-6 shadow-md hover:shadow-xl hover:-translate-y-2 transition-all duration-300 w-full">
        <div className="flex items-center mb-3">
          <div className="bg-sky-700 p-2 rounded-lg mr-3 shrink-0">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <h4 className="font-bold text-black text-lg">Contact Numbers</h4>
        </div>
        
        <div className="flex flex-row gap-0 flex-wrap">
  {contacts.map((contact, index) => (
    <div
      key={index}
      className={`flex items-center p-3 rounded-lg transition-all duration-300 ${
        hoveredIndex === index ? 'bg-sky-50 scale-[1.02]' : ''
      }`}
      onMouseEnter={() => setHoveredIndex(index)}
      onMouseLeave={() => setHoveredIndex(null)}
    >
      <div className="mr-3 text-lg">
        {contact.icon}
      </div>
      <a
        href={`tel:${contact.number.replace(/[^0-9+]/g, '')}`}
        className={`text-sky-600 text-base font-medium px-4 py-2 rounded transition-all duration-300 ${
          hoveredIndex === index ? 'bg-sky-100' : 'bg-sky-50'
        }`}
      >
        {contact.number}
      </a>
    </div>
  ))}
</div>

      </div>
    </div>
                </div>
              </div>
            </div>

            {/* Quick Links with unique styling */}
            <div className={`transition-all duration-700 ease-out delay-500 ${fadeInClass}`}>
              <h3 className="text-white font-bold text-xl mb-6 relative inline-block">
                Quick Links
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-white rounded-full"></span>
              </h3>
              <ul className="space-y-3">
                {footerLinks[0].items.map((item, idx) => (
                  <li 
                    key={idx}
                    className="transform hover:translate-x-2 transition-transform duration-300 border-b border-sky-600/30 pb-2"
                  >
                    <Link 
                      to={item === "Home" ? "/" : `/${item.toLowerCase().replace(/\s+/g, '-')}`} 
                      className="text-white hover:text-sky-200 flex items-center group"
                    >
                      <div className="w-6 h-6 bg-sky-100 rounded-full flex items-center justify-center mr-2 transform group-hover:scale-110 transition-transform duration-300">
                        <svg className="w-3 h-3 text-sky-700" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <span>{item}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Our Services with contrasting style */}
            <div className={`transition-all duration-700 ease-out delay-600 ${fadeInClass}`}>
              <h3 className="text-white font-bold text-xl mb-6 relative inline-block">
                {footerLinks[1].title}
                <span className="absolute -bottom-2 left-0 w-full h-1 bg-white rounded-full"></span>
              </h3>
              <ul className="space-y-3">
                {footerLinks[1].items.map((item, idx) => (
                  <li 
                    key={idx}
                    className="transform hover:translate-x-2 transition-transform duration-300 border-b border-sky-600/30 pb-2"
                  >
                    <Link 
                      to={`/${item.toLowerCase().replace(/\s+/g, '-')}`} 
                      className="text-white hover:text-sky-200 flex items-center group"
                    >
                      <span className="flex items-center justify-center w-8 h-8 bg-white/20 rounded-full mr-3 group-hover:bg-white/40 transition-all duration-300">
                        {item === "Eye Care" ? (
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                        )}
                      </span>
                      {item}
                    </Link>
                  </li>
                ))}
                <li className="pt-4 mt-2 border-t border-sky-600/30">
                  <Link to="/specialities" className="bg-white text-sky-800 px-4 py-2 rounded-lg hover:bg-sky-100 transition-colors duration-300 flex items-center justify-center font-medium text-sm">
                    View All Services
                    <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar with cleaner design */}
      <div className="bg-black text-white py-4">
        <div className={`container mx-auto px-6 flex flex-col justify-center items-center text-center transition-all duration-700 ease-out delay-800 ${fadeInClass}`}>
          <p className="text-sm text-gray-400">
            Copyright © {new Date().getFullYear()} <span className="text-white">Unity Ortho and Eye Hospital</span>. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;